import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {MapNode, DisconnectedMapNode} from 'common/src/modules/nodes/nodes.types';
import {DTCreativeNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {CreativeNodePlaceholderCreativeId} from 'common/src/modules/decisionTree/decisionTree.types';
import isCreativeTypeNode from 'common/src/components/CLCardsMap/utils/isCreativeTypeNode';

type DuplicationResult = {
  duplicatedNodes: Array<{originalId: number; newId: number; node: DisconnectedMapNode}>;
  nextNodeId: number;
};

/**
 * Recursively duplicates a subtree of campaign cards.
 * Stops recursion at creative nodes or when no outputs exist.
 *
 * @param originalNode - The node to duplicate
 * @param currentNextNodeId - The next available node ID
 * @param allNodes - Map of all nodes in the campaign (for traversal)
 * @param xOffset - Horizontal offset for positioning (applied to root only)
 * @param yOffset - Vertical offset for positioning (applied to root only)
 * @param isRoot - Whether this is the root node of the duplication
 * @returns Object containing duplicated nodes and the next available node ID
 */
export default function duplicateSubtreeRecursive(
  originalNode: Omit<MapNode, 'node' | 'inputs'>,
  currentNextNodeId: number,
  allNodes: Record<number, Omit<MapNode, 'node' | 'inputs'>>,
  xOffset = 50,
  yOffset = 50,
  isRoot = true,
): DuplicationResult {
  const duplicatedNodes: DuplicationResult['duplicatedNodes'] = [];

  // Create duplicate of current node with new ID
  const newNodeId = currentNextNodeId;

  // Copy outputs with all their properties preserved
  // The nodeId will be remapped later in Phase 5 of the main tool
  const duplicatedOutputs = originalNode.outputs.map((output) => ({
    ...output,
  }));

  const duplicatedNode: DisconnectedMapNode = {
    ...originalNode,
    id: newNodeId,
    // Apply offset only to root node
    x: isRoot && originalNode.x !== undefined ? originalNode.x + xOffset : originalNode.x,
    y: isRoot && originalNode.y !== undefined ? originalNode.y + yOffset : originalNode.y,
    inputs: [], // Will be rebuilt later based on connections
    outputs: duplicatedOutputs, // Preserve all output properties including nodeId for mapping
  };

  // Clear creativeId for creative nodes so they don't reference the same creative
  if (isCreativeTypeNode(originalNode.type) && 'creativeId' in duplicatedNode) {
    const creativeNode = duplicatedNode as Omit<
      DTCreativeNode,
      'node' | 'inputs' | 'landingPageSource'
    >;
    creativeNode.creativeId = CreativeNodePlaceholderCreativeId;
    creativeNode.creative = undefined;

    duplicatedNodes.push({
      originalId: originalNode.id,
      newId: newNodeId,
      node: {
        ...creativeNode,
        inputs: [],
        outputs: [],
      },
    });
  } else {
    duplicatedNodes.push({
      originalId: originalNode.id,
      newId: newNodeId,
      node: duplicatedNode,
    });
  }

  let nextAvailableId = currentNextNodeId + 1;

  // Check if we should stop recursion
  const shouldStopRecursion =
    isCreativeTypeNode(originalNode.type) || originalNode.outputs.length === 0;

  if (shouldStopRecursion) {
    return {duplicatedNodes, nextNodeId: nextAvailableId};
  }

  // Recursively duplicate all child nodes
  for (const output of originalNode.outputs) {
    // Skip disconnected outputs
    if (output.nodeId === NO_NODE_ID) {
      continue;
    }

    const childNode = allNodes[output.nodeId];

    if (!childNode) {
      continue;
    }

    // Recursively duplicate child node (not root anymore)
    const childResult = duplicateSubtreeRecursive(
      childNode,
      nextAvailableId,
      allNodes,
      xOffset,
      yOffset,
      false,
    );

    // Collect child duplicated nodes
    duplicatedNodes.push(...childResult.duplicatedNodes);

    // Update next available ID
    nextAvailableId = childResult.nextNodeId;
  }

  return {duplicatedNodes, nextNodeId: nextAvailableId};
}
